import type { ReactNode } from 'react';

import { Button, type ButtonVariant } from '@/components/common/button';

import styles from './overlay-chrome.module.scss';

export type OverlayFooterProps = {
  primaryText?: string;
  onPrimary?: () => void;
  /** When true the primary button is `type="submit"` (used by FormModal/FormDrawer). */
  isSubmit?: boolean;
  primaryVariant?: ButtonVariant;
  primaryDanger?: boolean;
  loading?: boolean;
  disabled?: boolean;
  /** id of the element explaining why the primary button is unavailable. */
  describedBy?: string;
  /** Content at the start of the footer row, opposite the buttons (e.g. a form's checkbox). */
  start?: ReactNode;
  cancelText?: string;
  onCancel?: () => void;
  /** `fill`: the two buttons split the row in half, Cancel outlined, on the card itself — the
   *  confirm look of Figma 1422:612060 (US-117535). */
  align?: 'end' | 'center' | 'fill';
};

export function OverlayFooter({
  primaryText,
  onPrimary,
  isSubmit = false,
  primaryVariant = 'primary',
  primaryDanger = false,
  loading = false,
  disabled = false,
  describedBy,
  cancelText = 'Cancel',
  onCancel,
  align = 'end',
  start,
}: OverlayFooterProps) {
  const showCancel = Boolean(cancelText && onCancel);
  if (!primaryText && !showCancel && !start) return null;

  return (
    <div
      className={[
        styles.footer,
        align === 'center' ? styles.footerCenter : '',
        align === 'fill' ? styles.footerFill : '',
      ].join(' ')}
    >
      {start ? <div className={styles.footerStart}>{start}</div> : null}
      {showCancel ? (
        <Button
          variant={align === 'fill' ? 'outline' : 'secondary'}
          onClick={onCancel}
          disabled={loading}
        >
          {cancelText}
        </Button>
      ) : null}
      {primaryText ? (
        <Button
          htmlType={isSubmit ? 'submit' : 'button'}
          variant={primaryVariant}
          onClick={isSubmit ? undefined : onPrimary}
          loading={loading}
          disabled={disabled}
          aria-describedby={describedBy}
          className={primaryDanger ? styles.danger : undefined}
        >
          {primaryText}
        </Button>
      ) : null}
    </div>
  );
}
